import { useMemo } from "react";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { useOnboardingUnlockedDestinations } from "@-/dev.improve.haniverse.lib.onboarding";
import { applyOnboardingUnlockedDestinations } from "@-/dev.improve.haniverse.lib.onboarding.state";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";
import type { PrimaryNavigationDestination } from "@-/dev.improve.haniverse.database.schemas.actor.schema._tables.user";
import {
  getAvailablePrimaryNavigationDestinations,
  resolvePrimaryNavigationStripDestinations,
} from "#primary-navigation-strip-destinations";

/** Destinations this device may place in the strip, in canonical order. */
export function useAvailablePrimaryNavigationDestinations(): readonly PrimaryNavigationDestination[] {
  const [isCookbookEnabled] = useDeviceSetting("isCookbookEnabled");
  return useMemo(
    () => getAvailablePrimaryNavigationDestinations({ isCookbookEnabled }),
    [isCookbookEnabled],
  );
}

/**
 * The saved strip, in the user's order, limited to what this device can show. While a new
 * learner's onboarding is under way it is limited further, to what the onboarding has unlocked so
 * far (`lib/onboarding`): the map alone at first (Learn's tab, presented as the map), then each
 * destination as the tour reaches it.
 */
export function usePrimaryNavigationStripDestinations(): readonly PrimaryNavigationDestination[] {
  const { primaryNavigationDestinations } = useActorUser();
  const available = useAvailablePrimaryNavigationDestinations();
  const onboardingUnlocked = useOnboardingUnlockedDestinations();
  return useMemo(
    () =>
      applyOnboardingUnlockedDestinations(
        resolvePrimaryNavigationStripDestinations(primaryNavigationDestinations, available),
        onboardingUnlocked,
      ),
    [available, onboardingUnlocked, primaryNavigationDestinations],
  );
}
